import React, { useEffect, useState, useCallback } from "react";
import Navbar from "./Navbar";
import "./EventMaster.css";
import authStore from "../services/authStore";
import toast from "react-hot-toast";
import { jwtDecode } from "jwt-decode";

export default function EventMaster() {
  const [eventList, setEventList] = useState([]);
  const [eventName, setEventName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [editName, setEditName] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editStatus, setEditStatus] = useState("ACTIVE");
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [addStatus, setAddStatus] = useState("ACTIVE");
  const [viewMode, setViewMode] = useState("LIST");
  const [statusFilter, setStatusFilter] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const getUserIdFromToken = useCallback(() => {
    const token = authStore.getToken();
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.user_id || decoded.userId || decoded.id;
    } catch (err) {
      console.error("Token decode error:", err);
      return null;
    }
  }, []);

  // Normalize API response helper function
  const normalizeEventData = (event) => {
    // Log the raw event to debug
    console.log("Normalizing event:", event);

    return {
      event_id: event.event_id || event.id || event.ID, // Keep original event_id
      ID: event.event_id || event.id || event.ID, // For consistency
      NAME: event.eventname || event.NAME || event.name,
      START_DATE: event.startdate || event.START_DATE || event.start_date,
      END_DATE: event.enddate || event.END_DATE || event.end_date,
      STATUS: (event.status || event.STATUS || "INACTIVE").toUpperCase(),
      IMAGE: event.image || event.IMAGE || null,
    };
  };

  // Fetch event list with normalization
  const fetchEventList = useCallback(async () => {
    const token = authStore.getToken();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    try {
      let url = `${API_BASE_URL}/event/GetEventList`;
      if (statusFilter) {
        url += `?status=${statusFilter}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("API request failed");

      const data = await response.json();
      console.log("API Response:", data); // Debug log

      const list = Array.isArray(data)
        ? data
        : data?.data || data?.result || [];

      // Normalize all events
      const formatted = list.map(normalizeEventData);

      // Sort by ID in descending order
      const sortedList = formatted.sort((a, b) => b.ID - a.ID);
      setEventList(sortedList);
    } catch (err) {
      console.error("API Error:", err.message);
      toast.error("Failed to fetch event list");
    }
  }, [API_BASE_URL, statusFilter]);

  useEffect(() => {
    fetchEventList();
  }, [fetchEventList]);

  const handleImageChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      if (isEdit) {
        setEditImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setEditImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleSave = async () => {
    if (!eventName.trim()) {
      toast.warning("Please enter event name");
      return;
    }

    if (!startDate) {
      toast.warning("Please select start date");
      return;
    }

    if (!endDate) {
      toast.warning("Please select end date");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.warning("Start date cannot be after end date");
      return;
    }

    const formData = new FormData();
    formData.append("eventname", eventName);
    formData.append("startdate", startDate);
    formData.append("enddate", endDate);
    formData.append("status", addStatus === "ACTIVE" ? "Active" : "Inactive");
    if (image) {
      formData.append("image", image);
    }

    const token = authStore.getToken();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/event/AddEvent`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Save error:", errorData);
        throw new Error(errorData.detail?.[0]?.msg || "Save failed");
      }

      const data = await response.json();
      console.log("Save response:", data);

      // Normalize the response to match UI structure
      const normalizedEvent = {
        event_id: data.event_id || data.id,
        ID: data.event_id || data.id,
        NAME: eventName,
        START_DATE: startDate,
        END_DATE: endDate,
        STATUS: addStatus,
        IMAGE: imagePreview,
      };

      // Add new event to list and sort descending
      setEventList((prev) => {
        const updatedList = [normalizedEvent, ...prev];
        return updatedList.sort((a, b) => b.ID - a.ID);
      });

      // Reset form
      setEventName("");
      setStartDate("");
      setEndDate("");
      setImage(null);
      setImagePreview(null);
      setAddStatus("ACTIVE");
      setViewMode("LIST");

      toast.success(`Event "${eventName}" added successfully`);
    } catch (err) {
      console.error("Save Error:", err.message);
      toast.error("Failed to save event");
    }
  };

  const handleEdit = (event) => {
    console.log("Editing event:", event); // Debug log
    setEditEvent(event);
    setEditName(event.NAME);
    setEditStartDate(event.START_DATE?.split("T")[0] || event.START_DATE);
    setEditEndDate(event.END_DATE?.split("T")[0] || event.END_DATE);
    setEditStatus(event.STATUS);
    setEditImagePreview(event.IMAGE);
    setEditImage(null);
    setViewMode("EDIT");
  };

  const handleUpdate = async () => {
    // Debug log to check editEvent
    console.log("Edit event object:", editEvent);
    console.log("Event ID:", editEvent?.event_id || editEvent?.ID);

    if (!editEvent || (!editEvent.event_id && !editEvent.ID)) {
      toast.error("Invalid event data. Please try editing again.");
      console.error("Edit event missing ID:", editEvent);
      return;
    }

    const eventId = editEvent.event_id || editEvent.ID;
    console.log("Using event ID:", eventId);

    if (!editName.trim()) {
      toast.warning("Please enter event name");
      return;
    }

    if (!editStartDate) {
      toast.warning("Please select start date");
      return;
    }

    if (!editEndDate) {
      toast.warning("Please select end date");
      return;
    }

    if (new Date(editStartDate) > new Date(editEndDate)) {
      toast.warning("Start date cannot be after end date");
      return;
    }

    const formData = new FormData();
    formData.append("eventname", editName);
    formData.append("startdate", editStartDate);
    formData.append("enddate", editEndDate);
    formData.append("status", editStatus === "ACTIVE" ? "Active" : "Inactive");
    if (editImage) {
      formData.append("image", editImage);
    }

    const token = authStore.getToken();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    try {
      const url = `${API_BASE_URL}/event/UpdateEvent/${eventId}`;
      console.log("Update URL:", url);

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Update error:", errorData);
        throw new Error(errorData.detail?.[0]?.msg || "Update failed");
      }

      const data = await response.json();
      console.log("Update response:", data);

      // Update UI instantly
      setEventList((prev) => {
        const updatedList = prev.map((item) =>
          item.event_id === eventId || item.ID === eventId
            ? {
              ...item,
              NAME: editName,
              START_DATE: editStartDate,
              END_DATE: editEndDate,
              STATUS: editStatus,
              IMAGE: editImagePreview || item.IMAGE,
            }
            : item,
        );
        return updatedList.sort((a, b) => b.ID - a.ID);
      });

      setViewMode("LIST");
      setEditEvent(null);
      setEditImage(null);
      setEditImagePreview(null);
      toast.success("Event updated successfully");
    } catch (err) {
      console.error("Update Error:", err.message);
      toast.error(`Failed to update event: ${err.message}`);
    }
  };

  const handleStatusToggle = async (event) => {
    const newStatus = event.STATUS === "ACTIVE" ? "Inactive" : "Active";
    const eventId = event.event_id || event.ID;

    const token = authStore.getToken();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("status", newStatus);

      const response = await fetch(
        `${API_BASE_URL}/event/UpdateEventStatus/${eventId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Status update error:", errorData);
        throw new Error(errorData.detail?.[0]?.msg || "Status update failed");
      }

      await response.json();

      // update UI
      setEventList((prev) =>
        prev.map((item) =>
          item.event_id === eventId || item.ID === eventId
            ? { ...item, STATUS: newStatus.toUpperCase() }
            : item,
        ),
      );

      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      console.error("Status Error:", err.message);
      toast.error("Failed to update status");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <>
      <Navbar />
      <div className="Pagename">
        <div>Event Master</div>
      </div>

      <div className="Secondbar">
        {viewMode !== "LIST" && (
          <button
            className="back-circle-btn"
            onClick={() => setViewMode("LIST")}
            title="Back"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
        )}
        {viewMode === "LIST" && (
          <div
            className="top-bar"
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "12px",
              marginBottom: "10px",
            }}
          >
            <button
              className="plus-btn"
              onClick={() => {
                setViewMode("ADD");
                setEventName("");
                setStartDate("");
                setEndDate("");
                setImage(null);
                setImagePreview(null);
                setAddStatus("ACTIVE");
              }}
            >
              + Add Event
            </button>
          </div>
        )}
      </div>

      {viewMode === "LIST" && (
        <div className="table-container">
          <table className="event-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Event Name</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {eventList.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    No data found
                  </td>
                </tr>
              ) : (
                eventList.map((item) => (
                  <tr key={item.event_id || item.ID}>
                    <td>{item.event_id || item.ID}</td>
                    <td>{item.NAME}</td>
                    <td>{formatDate(item.START_DATE)}</td>
                    <td>{formatDate(item.END_DATE)}</td>

                    <td>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={item.STATUS === "ACTIVE"}
                          onChange={() => handleStatusToggle(item)}
                        />
                        <span className="slider"></span>
                      </label>
                    </td>
                    <td className="action-col">
                      <i
                        className="fa-regular fa-pen-to-square fa-lg edit-icon"
                        onClick={() => handleEdit(item)}
                        title="Edit Event"
                        style={{ cursor: "pointer" }}
                      ></i>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === "ADD" && (
        <div className="addview">
          <h4>Add Event</h4>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <label>Event Name</label>
            <input
              type="text"
              placeholder="Enter event name"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <label>Event Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, false)}
            />
            {imagePreview && (
              <div style={{ marginTop: "10px" }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                />
              </div>
            )}
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <span>Status</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={addStatus === "ACTIVE"}
                onChange={() =>
                  setAddStatus(addStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE")
                }
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="popup-buttons">
            <button className="btn-save" onClick={handleSave}>
              Save
            </button>
            <button
              className="btn-cancel"
              onClick={() => {
                setEventName("");
                setStartDate("");
                setEndDate("");
                setImage(null);
                setImagePreview(null);
                setAddStatus("ACTIVE");
                setViewMode("LIST");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {viewMode === "EDIT" && (
        <div className="addview">
          <h4>Edit Event</h4>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span>Event Name</span>
            <input
              type="text"
              placeholder="Enter event name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span>Start Date</span>
            <input
              type="date"
              value={editStartDate}
              onChange={(e) => setEditStartDate(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span>End Date</span>
            <input
              type="date"
              value={editEndDate}
              onChange={(e) => setEditEndDate(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span>Event Image</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, true)}
            />
            {editImagePreview && (
              <div style={{ marginTop: "10px" }}>
                <img
                  src={editImagePreview}
                  alt="Preview"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                />
              </div>
            )}
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <span>Status</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={editStatus === "ACTIVE"}
                onChange={() =>
                  setEditStatus(editStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE")
                }
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="popup-buttons">
            <button className="btn-save" onClick={handleUpdate}>
              Update
            </button>
            <button
              className="btn-cancel"
              onClick={() => {
                setViewMode("LIST");
                setEditEvent(null);
                setEditImage(null);
                setEditImagePreview(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
